import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { PrescriptionService } from '../../services/prescription.service';
import { LoaderService } from '../../services/loader.service';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { Medicine } from 'src/interfaces/medicine';
import { DosageType } from 'src/interfaces/dosage-type';
import { MedicineDosagePackageType } from 'src/interfaces/medicine-dosage-package-type';
import {
  Individual,
  IndividualEditPayload,
  IndividualType,
} from 'src/interfaces/individual';
import {PrintDeliveryLabelPayload} from '../../../interfaces/waybill';
import { DeliveryType } from '../../../interfaces/delivery-type';
import { Address } from '../../../interfaces/address';
import { MatDialog } from '@angular/material/dialog';
import { OrderItem, OrderItemStatus } from 'src/interfaces/order-item';
import { CanAddAndRemoveOrderItem } from 'src/interfaces/can-add-and-remove-order-item';
import { Order, EditOrderFormData } from 'src/interfaces/order';
import { all_countries, Country } from 'src/form/components/data/countries';
import { all_provinces, Province } from 'src/form/components/data/provinces';
import { MatStepper } from '@angular/material/stepper';
import { PresignedUrlService } from 'src/core/services/presigned-url.service';
import { environment } from 'src/environments/environment';
import { BatchStatus } from 'src/interfaces/batch';
import { ShippingDetails } from 'src/interfaces/shipping-details';
import { DirtinessService } from 'src/core/services/dirtiness.service';
import { ShippingService } from 'src/core/services/shipping.service';
import { OrderStatus } from 'src/interfaces/order-status';
import { MatSelectChange } from '@angular/material/select';
import {
  Company,
  CompanyType,
  EditCompanyPayload,
} from 'src/interfaces/company';
import { UtilsService } from 'src/core/services/utils.service';
import { CompanyService } from 'src/core/services/company.service';
import { IndividualService } from 'src/core/services/individual.service';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { OtpService } from 'src/core/services/otp.service';
import { OtpData } from 'src/interfaces/otp.interface';
import { SageService } from 'src/core/services/sage.service';
import { sageSyncLabelFromOrder } from 'src/interfaces/sage-sync-status';

/** Order status codes that trigger sync to Sage (confirm with backend). */
const SAGE_SYNC_TRIGGER_STATUS_CODES = ['Approved', 'APR'];

interface UserAttribute {
  Name: string;
  Value: string;
}

@Component({
  selector: 'app-prescription-review',
  templateUrl: './prescription-review.component.html',
  styleUrls: ['./prescription-review.component.scss'],
})
export class PrescriptionReviewComponent
  implements OnInit, CanAddAndRemoveOrderItem
{
  order: Order | null = null;
  company: Company | null = null;
  individual: Individual | null = null;
  prescriber: Individual | null = null;

  editingIndividual: boolean = false;
  editingCompany: boolean = false;
  editingPrescriber: boolean = false;
  editingAddress: boolean = false;

  countries: Country[] = all_countries;
  filteredCountries: Observable<Country[]>;
  provinces: Province[] = all_provinces;
  @ViewChild('stepper') private _stepper: MatStepper;
  companyTypes: CompanyType[] = [];
  selectedCompany: Company | null = null;
  selectedPrescriber: Individual | null = null;
  selectedDistributor: Company | null = null;
  showDistributor: boolean = false;

  entityLayoutClass: {
    'col-md-6': boolean;
    'col-md-4': boolean;
  } = {
    'col-md-6': true,
    'col-md-4': true,
  };

  individualDetailsForm = this._formBuilder.group({
    title: ['', Validators.required],
    initials: ['', Validators.required],
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    gender: ['', Validators.required],
    dateOfBirth: ['', Validators.required],
    phoneNumber: [
      '',
      Validators.compose([
        Validators.required,
        Validators.pattern('^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-s./0-9]*$'),
      ]),
    ],
    phoneNumberAlt: [
      '',
      Validators.pattern('^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-s./0-9]*$'),
    ],
    email: ['', Validators.compose([Validators.required, Validators.email])],
    emailAlt: ['', Validators.email],
    individualType: ['', Validators.required],
    numberOfDependants: ['', Validators.compose([Validators.min(0)])],
    buildingAptNumber: ['', Validators.required],
    addressLine1: ['', Validators.required],
    addressLine2: [''],
    province: ['', Validators.required],
    postcode: [
      '',
      Validators.compose([
        Validators.required,
        Validators.pattern(/^[0-9]{4,5}$/),
      ]),
    ],
    country: ['South Africa', Validators.required],
    deliveryType: ['', Validators.required],
    isAnimal: ['', Validators.required],
  });

  prescriberDetailsForm = this._formBuilder.group({
    title: ['', Validators.required],
    initials: ['', Validators.required],
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    gender: ['', Validators.required],
    dateOfBirth: ['', Validators.required],
    phoneNumber: [
      '',
      Validators.compose([
        Validators.required,
        Validators.pattern('^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-s./0-9]*$'),
      ]),
    ],
    phoneNumberAlt: [
      '',
      Validators.pattern('^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-s./0-9]*$'),
    ],
    email: ['', Validators.compose([Validators.required, Validators.email])],
    emailAlt: ['', Validators.email],
    individualType: ['', Validators.required],
    numberOfDependants: ['', Validators.compose([Validators.min(0)])],
    buildingAptNumber: ['', Validators.required],
    addressLine1: ['', Validators.required],
    addressLine2: [''],
    province: ['', Validators.required],
    postcode: [
      '',
      Validators.compose([
        Validators.required,
        Validators.pattern(/^[0-9]{4,5}$/),
      ]),
    ],
    country: ['South Africa', Validators.required],
    deliveryType: ['', Validators.required],
    isAnimal: ['', Validators.required],
  });

  companyDetailsForm = this._formBuilder.group({
    companyName: ['', Validators.required],
    email: ['', Validators.compose([Validators.required, Validators.email])],
    emailAlt: ['', Validators.email],
    phoneNumber: [
      '',
      Validators.compose([
        Validators.required,
        Validators.pattern('^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-s./0-9]*$'),
      ]),
    ],
    phoneNumberAlt: [
      '',
      Validators.pattern('^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-s./0-9]*$'),
    ],
    practiceNumber: ['', Validators.required],
    vatRegistrationNumber: ['', Validators.pattern(/[0-9]+/)],
    typeOfBusiness: ['', Validators.required],
    distributor: [''],
  });

  addressDetailsForm = this._formBuilder.group({
    buildingAptNumber: ['', Validators.required],
    addressLine1: ['', Validators.required],
    addressLine2: [''],
    province: ['', Validators.required],
    postcode: [
      '',
      Validators.compose([
        Validators.required,
        Validators.pattern(/^[0-9]{4,5}$/),
      ]),
    ],
    city: [''],
    country: ['South Africa', Validators.required],
    deliveryType: ['', Validators.required],
  });

  deliveryTypes: DeliveryType[] = [];
  addresses: Address[] = [];

  entityForm: FormGroup = this._formBuilder.group({
    individualType: ['', Validators.required],
    filter: [''],
  });

  // Medicine
  allMedicine: Medicine[] = [];
  dosageTypes: DosageType[] = [];
  medicineDosageTypePackageTypes: MedicineDosagePackageType[] = [];
  filteredMedicine: Observable<Medicine[]>;
  selectedMedicine: Medicine | null = null;
  selectedDosageType: DosageType | null = null;
  orderItems: OrderItem[] = [];
  removedItems: OrderItem[] = [];

  // Entities
  individualTypes: IndividualType[] = [];
  entities: any = [];
  filteredEntities: Observable<any[]>;
  selectedEntity: any;
  isLoading: boolean = false;
  orderSummaryUrl: string | null = null;

  orderItemStatuses: OrderItemStatus[] = [];
  batchStatuses: BatchStatus[] = [];
  selectedStepIndex: number = 0;

  isShipped: boolean = false;
  orderStatuses: OrderStatus[] = [];
  selectedStatus: OrderStatus | null = null;
  orderStatusControl = this._formBuilder.control(null, Validators.required);

  readonly sageUiEnabled = environment.sageUiEnabled;

  @ViewChild(RouterOutlet) outlet: RouterOutlet;

  constructor(
    private _prescriptionService: PrescriptionService,
    private _presignedUrlService: PresignedUrlService,
    private _loaderService: LoaderService,
    private _route: ActivatedRoute,
    private _router: Router,
    private _formBuilder: FormBuilder,
    public _dialog: MatDialog,
    private _dirtinessService: DirtinessService,
    private _shippingService: ShippingService,
    public utilsService: UtilsService,
    private _companyService: CompanyService,
    private _individualService: IndividualService,
    private _otpService: OtpService,
    private _sageService: SageService
  ) {}

  async ngOnInit() {
    this._loaderService.isLoading.subscribe((isLoading) => {
      this.isLoading = isLoading;
    });

    this._route.data.subscribe((data) => {
      this.order = data.order.order;
      this.individualTypes = data.order.extras.individual_type;
      this.deliveryTypes = data.order.extras.delivery_type;
      this.orderItemStatuses = data.order.extras.order_item_status;
      this.batchStatuses = data.order.extras.batch_status;
      this.orderStatuses = data.order.extras.order_status;
      this.companyTypes = data.order.extras.company_type;

      if (!this.order) return;
      this.updateView(this.order);
    });

    this.filteredCountries = this.addressDetailsForm.controls[
      'country'
    ].valueChanges.pipe(
      startWith('South Africa'),
      map((value) => this._filterCountries(value))
    );

    this.individualDetailsForm.valueChanges.subscribe((value) => {
      this._dirtinessService.setDirty(true);
    });

    this.companyDetailsForm.controls['typeOfBusiness'].valueChanges.subscribe(
      (value) => {
        // choose to show or not show distributor field
        const companyType: CompanyType | undefined = this.companyTypes.find(
          (type) => type.id === value
        );
        this.showDistributor = companyType
          ? companyType.code !== 'DIS'
            ? true
            : false
          : false;
        if (companyType) this.toggleDistributor(companyType);
      }
    );
  }

  toggleDistributor(companyType: CompanyType) {
    if (companyType.code === 'DIS') {
      this.showDistributor = false;
      this.companyDetailsForm.patchValue({
        distributor: null,
      });
    } else {
      this.showDistributor = true;
    }
  }

  _filterCountries(value: string) {
    return this.countries.filter((country) => {
      return country.name.toLowerCase().includes(value.toLowerCase());
    });
  }

  ngOnChanges(): void {}

  refresh() {
    (async () => {
      if (!this.order) return;

      this.order = await this._prescriptionService.getOrder(this.order.id);
      if (!this.order) return;

      this.updateView(this.order);
    })();
  }

  updateView(order: Order): void {
    this.orderItems = this.getOrderItems(order);
    this.removedItems = [];

    // Get order status
    const selectedStatus: OrderStatus | undefined = this.orderStatuses.find(
      (status) => status.id === order.order_status_id
    );

    this.selectedStatus = selectedStatus ? selectedStatus : null;

    this.orderStatusControl.patchValue(this.selectedStatus);

    if (order.patient) this.individual = order.patient;

    if (order.doctor) this.individual = order.doctor;

    if (this.individual)
      this.individualDetailsForm.patchValue({
        title: this.individual.title,
        initials: this.individual.initials,
        firstName: this.individual.first_name,
        lastName: this.individual.last_name,
        gender: this.individual.gender,
        isAnimal: Boolean(this.individual.is_animal),
        dateOfBirth: this.individual.date_of_birth,
        phoneNumber: this.individual.phone_number,
        phoneNumberAlt: this.individual.phone_number_alt,
        email: this.individual.email,
        emailAlt: this.individual.email_alt,
        individualType: this.individual.individual_type_id,
        numberOfDependants: this.individual.dependant_number,
      });

    if (order.client) this.company = order.client;

    if (order.doctor)
      if (order.doctor.company) this.company = order.doctor.company;

    if (order.patient)
      if (order.patient.company) this.company = order.patient.company;

    if (this.company) {
      this.selectedCompany = this.company;
      const companyType: CompanyType | undefined = this.companyTypes.find(
        (type) => {
          if (!this.company) return false;

          return type.id === this.company.company_type_id;
        }
      );

      if (companyType) this.toggleDistributor(companyType);

      this.companyDetailsForm.patchValue({
        companyName: this.company.name,
        email: this.company.email,
        emailAlt: this.company.email_alt,
        phoneNumber: this.company.phone_number,
        phoneNumberAlt: this.company.phone_number_alt,
        practiceNumber: this.company.practice_number,
        vatRegistrationNumber: this.company.vat_registration_number,
        typeOfBusiness: this.company.company_type_id,
      });
    }

    this.prescriber = order.prescriber;

    if (this.prescriber) {
      this.prescriberDetailsForm.patchValue({
        firstName: this.prescriber.first_name,
        lastName: this.prescriber.last_name,
        title: this.prescriber.title,
        initials: this.prescriber.initials,
        gender: this.prescriber.gender,
        isAnimal: false,
        dateOfBirth: this.prescriber.date_of_birth,
        phoneNumber: this.prescriber.phone_number,
        phoneNumberAlt: this.prescriber.phone_number_alt,
        email: this.prescriber.email,
        emailAlt: this.prescriber.email_alt,
        individualType: this.prescriber.individual_type_id,
        numberOfDependants: this.prescriber.dependant_number,
      });
    }

    if (!order.shipping_details) return;

    if (!order.shipping_details.address) return;

    const address: Address = order.shipping_details.address;
    const shipping_details: ShippingDetails = order.shipping_details;

    this.addressDetailsForm.patchValue({
      buildingAptNumber: address.building_apt_number,
      addressLine1: address.address_1,
      addressLine2: address.address_2,
      province: address.province,
      postcode: address.postcode,
      city: address.city,
      country: address.country,
      deliveryType: shipping_details.delivery_type_id,
    });

    this._dirtinessService.setDirty(false);
  }

  getSageSyncLabel(): string {
    return sageSyncLabelFromOrder(this.order);
  }

  async syncOrderToSage(): Promise<void> {
    if (!this.order) return;
    const ok = await this._sageService.syncOrderToSage(this.order.id);
    if (ok) await this.refresh();
  }

  getOrderItems(order: Order) {
    if (!order) return [];

    return order.order_items.map((item: any) => {
      const orderedItem = {
        id: item.id,
        order_id: item.order_id,
        medicineDosagePackageType: item.medicine_dosage_package_type,
        dosageType: item.medicine_dosage_package_type.medicine_dosage_type,
        status: item.status,
        medicine:
          item.medicine_dosage_package_type.medicine_dosage_type.medicine,
        quantity: item.total_quantity,
        medicine_dosage_package_type_id: item.medicine_dosage_package_type_id,
        quantity_fulfilled: item.quantity_fulfilled,
        deleted_at: undefined,
      };

      orderedItem.dosageType.default_price =
        item.medicine_dosage_package_type.medicine_dosage_type.default_price;

      return orderedItem;
    });
  }

  addOrderItem(item: OrderItem) {
    this.orderItems.push(item);
    this.orderItems = [...this.orderItems];
    // Oh yeah it's a nasty dirty service :P
    this._dirtinessService.setDirty(true);
  }

  removeOrderItem(item: OrderItem) {
    const index = this.orderItems.indexOf(item);

    if (index > -1) this.orderItems.splice(index, 1);
    this.orderItems = [...this.orderItems];

    item.deleted_at = new Date();

    if (item.id)
      // check if this is not a new item
      this.removedItems.push(item);

    this._dirtinessService.setDirty(true);
  }

  getEntityName(entity: any) {
    return entity.first_name
      ? `${entity.first_name} ${entity.last_name}`
      : entity.name;
  }

  save() {
    if (!this.order) {
      return;
    }

    let allItems: OrderItem[] = [];
    allItems = allItems.concat(this.orderItems, this.removedItems);

    const orderItems = allItems.map((item) => ({
      id: item.id ? item.id : undefined, // a new order item has an id of undefined
      quantity: item.quantity,
      medicine_dosage_package_type_id: item.medicineDosagePackageType.id,
      order_item_status_id: item.status ? item.status.id : null,
      deleted_at: item.deleted_at ? item.deleted_at : undefined,
    }));

    const status: OrderStatus = <OrderStatus>this.orderStatusControl.value;

    const payload: EditOrderFormData = {
      id: this.order.id,
      barcode: this.order.barcode,
      created_at: this.order.created_at,
      updated_at: this.order.updated_at,
      deleted_at: this.order.deleted_at,
      order_status_id: status.id,
      client_id: undefined, // set client id if order is for a company
      doctor_id: this.order.doctor_id,
      patient_id: this.order.patient_id,
      shipping_details_id: this.order.shipping_details_id,
      parent_order_id: this.order.parent_order_id,
      doctor: undefined,
      client: undefined,
      prescriber_id: undefined,
      prescriber: undefined,
      status: this.order.status,
      patient: undefined,
      shipping_details: {
        id: this.order.shipping_details.id,
        waybill_number: this.order.shipping_details.waybill_number,
        address_id: this.order.shipping_details.address_id,
        delivery_type_id: this.addressDetailsForm.value.deliveryType,
        address: {
          id: this.order.shipping_details.address.id,
          building_apt_number: this.addressDetailsForm.value.buildingAptNumber,
          address_1: this.addressDetailsForm.value.addressLine1,
          address_2: this.addressDetailsForm.value.addressLine2,
          province: this.addressDetailsForm.value.province,
          postcode: this.addressDetailsForm.value.postcode,
          country: this.addressDetailsForm.value.country,
          city: this.addressDetailsForm.value.city,
          individual_id: this.order.shipping_details.address.individual_id,
          created_at: this.order.shipping_details.address.created_at,
          updated_at: this.order.shipping_details.address.updated_at,
          deleted_at: this.order.shipping_details.address.deleted_at,
        },
      },
      medicine: orderItems,
    };

    // Edit individual data

    const isAnimal: boolean = Boolean(
      parseInt(this.individualDetailsForm.value.isAnimal)
    );

    // This order is for an individual
    if (this.individual || this.individualDetailsForm.valid) {
      const individualPayload: IndividualEditPayload = {
        id: this.individual ? this.individual.id : undefined,
        profile_number: this.individual
          ? this.individual.profile_number
          : undefined,
        first_name: this.individualDetailsForm.value.firstName,
        last_name: this.individualDetailsForm.value.lastName,
        initials: this.individualDetailsForm.value.initials,
        gender: this.individualDetailsForm.value.gender,
        title: this.individualDetailsForm.value.title,
        dependant_number: this.individualDetailsForm.value.numberOfDependants,
        date_of_birth: this.individualDetailsForm.value.dateOfBirth,
        is_animal: isAnimal,
        phone_number: this.individualDetailsForm.value.phoneNumber,
        phone_number_alt: this.individualDetailsForm.value.phoneNumberAlt,
        email: this.individualDetailsForm.value.email,
        email_alt: this.individualDetailsForm.value.emailAlt,
        company_id: this.individual ? this.individual.company_id : undefined,
        individual_type_id: this.individualDetailsForm.value.individualType,
        address_id: this.order.shipping_details.address_id,
        address: undefined,
      };

      if (this.order.patient) payload.patient = individualPayload;
      if (this.order.doctor) payload.doctor = individualPayload;
    }

    // This order is for a company
    if (this.company || this.companyDetailsForm.valid) {
      payload.client_id = this.selectedCompany
        ? this.selectedCompany.id
        : this.company
        ? this.company.id
        : undefined;
      const companyPayload: EditCompanyPayload = {
        id: this.selectedCompany
          ? this.selectedCompany.id
          : this.company
          ? this.company.id
          : undefined,
        name: this.companyDetailsForm.value.name,
        email: this.companyDetailsForm.value.email,
        email_alt: this.companyDetailsForm.value.emailAlt,
        phone_number: this.companyDetailsForm.value.phoneNumber,
        phone_number_alt: this.companyDetailsForm.value.phoneNumberAlt,
        practice_number: this.companyDetailsForm.value.practiceNumber,
        vat_registration_number:
          this.companyDetailsForm.value.vatRegistrationNumber,
        company_type_id: this.companyDetailsForm.value.typeOfBusiness,
        primary_contact_id: undefined,
        address: undefined,
        distributor_id: undefined,
      };

      payload.client = companyPayload;
    }

    if (
      this.prescriber ||
      this.selectedPrescriber ||
      this.prescriberDetailsForm.valid
    ) {
      payload.prescriber_id = this.selectedPrescriber
        ? this.selectedPrescriber.id
        : this.prescriber
        ? this.prescriber.id
        : undefined;

      const companyPayload: IndividualEditPayload = {
        id: this.selectedPrescriber
          ? this.selectedPrescriber.id
          : this.prescriber
          ? this.prescriber.id
          : undefined,
        profile_number: undefined,
        first_name: this.prescriberDetailsForm.value.firstName,
        last_name: this.prescriberDetailsForm.value.lastName,
        initials: this.prescriberDetailsForm.value.initials,
        gender: this.prescriberDetailsForm.value.gender,
        title: this.prescriberDetailsForm.value.title,
        dependant_number: this.prescriberDetailsForm.value.numberOfDependants,
        date_of_birth: this.prescriberDetailsForm.value.dateOfBirth,
        is_animal: this.prescriberDetailsForm.value.isAnimal,
        phone_number: this.prescriberDetailsForm.value.phoneNumber,
        phone_number_alt: this.prescriberDetailsForm.value.phoneNumberAlt,
        individual_type_id: this.prescriberDetailsForm.value.individualType,
        email: this.prescriberDetailsForm.value.email,
        email_alt: this.prescriberDetailsForm.value.emailAlt,
        company_id: this.selectedPrescriber
          ? this.selectedPrescriber.company_id
          : undefined,
        address_id: this.selectedPrescriber
          ? this.selectedPrescriber.address_id
          : undefined,
        address: undefined,
      };

      payload.prescriber = companyPayload;
    }

    (async () => {
      const success = await this._prescriptionService.editOrder(payload);
      if (success) {
        this._dirtinessService.setDirty(false);
        this.refresh();
        this.editingIndividual = false;
        this.editingCompany = false;
        this.editingPrescriber = false;
        this.editingAddress = false;
        // Sync to Sage when order status is one that triggers sync (do not block on failure)
        if (
          environment.sageUiEnabled &&
          this.order &&
          status?.code &&
          SAGE_SYNC_TRIGGER_STATUS_CODES.includes(status.code)
        ) {
          void this._sageService.syncOrderToSage(this.order.id);
        }
      }
    })();
  }

  submitData() {
    this.save();
  }

  get isValid() {
    return this.selectedEntity !== null && this.orderItems.length > 0;
  }

  getOrderSummaryData() {
    if (!this.order) {
      return null;
    }

    const items = this.order.order_items;

    if (items.length === 0) {
      return null;
    }

    const medicine = items.map((item) => ({
      contained_quantity: item.medicine_dosage_package_type.contained_quantity,
      description:
        item.medicine_dosage_package_type.medicine_dosage_type
          .additional_information,
      dosage_strength:
        item.medicine_dosage_package_type.medicine_dosage_type.dosage_strength,
      quantity: item.total_quantity,
      route_of_administration: 'Oral',
    }));

    return {
      template_name: 'order_summary',
      data: {
        status: this.order.status.status,
        barcode: this.order.id,
        medicine,
      },
      type: 'pdf',
    };
  }

  async generateOrderSummary() {
    return await this._prescriptionService.generateOrderSummary(
      this.getOrderSummaryData()
    );
  }

  async openOrderSummary() {
    this.orderSummaryUrl = await this.generateOrderSummary();

    let urlWithName;

    if (!this.orderSummaryUrl) return;

    urlWithName = this.orderSummaryUrl.replace(
      'data:application/pdf;',
      'data:application/pdf;name=Order Summary.pdf;'
    );

    if (urlWithName) {
      const html = `
      <html>
        <title>Order summary</title>
        <style>
        html, body { padding: 0; margin: 0; } iframe { width: 100%; height: 100%; border: 0;}
        </style>
        <body>
          <iframe type="application/pdf" src="${urlWithName}"></iframe>
        </body>
      </html>
      `;
      const orderSummaryWindow = window.open('about:blank', 'Zupfnoter');

      if (!orderSummaryWindow) return;

      orderSummaryWindow.document.write(html);
      orderSummaryWindow.document.close();
      orderSummaryWindow.focus();
    }
  }

  async openPrescriptionFile(order: Order | null) {
    if (!order) return;

    const id: number = order.parent_order_id ? order.parent_order_id : order.id;

    try {
      const { region, bucket, fileName, folder } = environment.s3;
      const key = `${folder}/${id}/${fileName}`;
      const response = await this._presignedUrlService.generateDownloadUrl(
        region,
        bucket,
        key
      );

      window.open(response.data.url, '_blank')?.focus();
    } catch (error) {
      console.error(error);

      return;
    }
  }

  get hasOrderItems() {
    return this.order ? this.order.order_items.length > 0 : false;
  }

  get entityName() {
    if (!this.order) return '';

    if (this.order.patient)
      return `${this.order.patient.first_name} ${this.order.patient.last_name}`;

    if (this.order.doctor)
      return this.order.doctor.company
        ? this.order.doctor.company.name
        : `${this.order.doctor.first_name} ${this.order.doctor.last_name}`;

    if (this.order.client) return this.order.client.name;

    return '';
  }

  get address() {
    if (!this.order) return null;

    return this.order.shipping_details.address;
  }

  get waybillNumber(): string | null {
    if (!this.order) return null;

    return this.order.shipping_details.waybill_number
      ? this.order.shipping_details.waybill_number
      : null;
  }
  get customerOrderNumber(): string | null {
    if (!this.order) return null;

    return this.order.customer_order_number
      ? this.order.customer_order_number
      : null;
  }

  get waybillWasCaptured(): boolean {
    return this.order
      ? this.order.shipping_details.waybill_number
        ? true
        : false
      : false;
  }

  get canCaptureWaybill(): boolean {
    if (!this.order) return false;

    return this.order.status.code === 'PKG' && !this.waybillWasCaptured;
  }

  getDeliveryType(order: Order | null): string {
    if (!order) return '-';

    const deliveryType: DeliveryType | undefined = this.deliveryTypes.find(
      (type) => {
        if (!order) return false;
        return type.id === order.shipping_details.delivery_type_id;
      }
    );

    return deliveryType ? deliveryType.type : '-';
  }

  get phoneNumber() {
    if (!this.order) return '';

    if (this.order.patient) return this.order.patient.phone_number;

    if (this.order.doctor)
      return this.order.doctor.company
        ? this.order.doctor.company.phone_number
          ? this.order.doctor.company.phone_number
          : ''
        : this.order.doctor.phone_number;

    if (this.order.client)
      return this.order.client
        ? this.order.client.phone_number
          ? this.order.client.phone_number
          : ''
        : '';

    return '';
  }

  get alternativePhoneNumber() {
    if (!this.order) return '';

    if (this.order.patient) return this.order.patient.phone_number_alt;

    if (this.order.doctor)
      return this.order.doctor.company
        ? this.order.doctor.company.phone_number_alt
          ? this.order.doctor.company.phone_number_alt
          : ''
        : this.order.doctor.phone_number_alt;

    if (this.order.client)
      return this.order.client
        ? this.order.client.phone_number_alt
          ? this.order.client.phone_number_alt
          : ''
        : '';

    return '';
  }

  get entityEmail() {
    if (!this.order) return '-';

    if (this.order.doctor)
      return this.order.doctor.email ? this.order.doctor.email : '-';
    if (this.order.client)
      return this.order.client.email ? this.order.client.email : '-';
    if (this.order.patient)
      return this.order.patient.email ? this.order.patient.email : '-';

    return '-';
  }

  get entityAlternativeEmail() {
    if (!this.order) return '-';

    if (this.order.doctor)
      return this.order.doctor.email_alt ? this.order.doctor.email_alt : '-';
    if (this.order.client)
      return this.order.client.email_alt ? this.order.client.email_alt : '-';
    if (this.order.patient)
      return this.order.patient.email_alt ? this.order.patient.email_alt : '-';

    return '-';
  }

  get isEntityAssigned(): boolean {
    if (!this.order) return false;

    return (
      this.order.patient !== null ||
      this.order.doctor !== null ||
      this.order.client !== null
    );
  }

  editDetails() {
    this._stepper.selectedIndex = this._stepper.selectedIndex === 0 ? 1 : 0;
    this.selectedStepIndex = this._stepper.selectedIndex;
  }

  getEditIndividualButtonText() {
    if (!this.isEntityAssigned && !this.editingIndividual)
      return 'Enter details';

    if (this.editingIndividual) return 'Details';

    return 'Edit';
  }

  getEditCompanyButtonText() {
    if (!this.isEntityAssigned && !this.editingCompany) return 'Enter details';

    if (this.editingCompany) return 'Details';

    return 'Edit';
  }

  getEditPrescriberButtonText() {
    if (!this.isEntityAssigned && !this.editingPrescriber)
      return 'Enter details';

    if (this.editingPrescriber) return 'Details';

    return 'Edit';
  }

  getEditAddressButtonText() {
    if (!this.address) return 'Enter details';

    return this.editingAddress ? 'Details' : 'Edit';
  }

  getEditButtonIcon() {
    if (this.selectedStepIndex === 1) return 'arrow_back';

    return 'edit';
  }

  get orderSplitLink() {
    return this.order ? `/portal/split/${this.order.id}` : '#';
  }

  get isFulfillable(): boolean {
    if (this.orderItems.length === 0) return false;

    return this.orderItems.every((item) => {
      if (!item.status) return false;
      return item.status.code === 'PRC' || item.status.code === 'PKD';
    });
  }

  async onOrderStatusChanged(event: MatSelectChange) {
    this._dirtinessService.setDirty(true);
    this.selectedStatus = event.value;
  }

  openOrderFulfilmentDialog() {
    if (!this.order) return;
    this._prescriptionService.openOrderFulfilmentDialog(
      this.order,
      this.orderItems
    );
  }

  openOrderTrackDialog(order: Order | null) {
    if (!order) return;
    this._shippingService.openOrderTrackDialog(order);
  }

  openCompany(company: Company) {
    const url = this._router.serializeUrl(
      this._router.createUrlTree([`/portal/companies/view/${company.id}`])
    );
    window.open(url, '_blank');
  }

  openIndividual(individual: Individual) {
    const url = this._router.serializeUrl(
      this._router.createUrlTree([`/portal/individuals/view/${individual.id}`])
    );
    window.open(url, '_blank');
  }

  async openShippingFormDialog(order: Order) {
    const success = await this._shippingService.openShippingFormDialog(order);

    if (success) this.refresh();
  }

  async openSearchCompanyDialog() {
    this.selectedCompany = await this._companyService.openCompanySearchDialog();

    if (!this.selectedCompany) return;

    this.companyDetailsForm.patchValue({
      companyName: this.selectedCompany.name,
      email: this.selectedCompany.email,
      emailAlt: this.selectedCompany.email_alt,
      phoneNumber: this.selectedCompany.phone_number,
      phoneNumberAlt: this.selectedCompany.phone_number_alt,
      practiceNumber: this.selectedCompany.practice_number,
      vatRegistrationNumber: this.selectedCompany.vat_registration_number,
      typeOfBusiness: this.selectedCompany.company_type_id,
    });

    const address: Address | null = this.selectedCompany.addresses
      ? this.selectedCompany.addresses.length > 0
        ? this.selectedCompany.addresses[0]
        : null
      : null;

    this.addressDetailsForm.patchValue({
      buildingAptNumber: address ? address.building_apt_number : '',
      addressLine1: address ? address.address_1 : '',
      addressLine2: address ? address.address_2 : '',
      province: address ? address.province : '',
      postcode: address ? address.postcode : '',
      country: address ? address.country : '',
    });
  }

  deselectCompany() {
    this.selectedCompany = null;
    this.companyDetailsForm.patchValue({
      companyName: '',
      email: '',
      emailAlt: '',
      phoneNumber: '',
      phoneNumberAlt: '',
      practiceNumber: '',
      vatRegistrationNumber: '',
      typeOfBusiness: '',
    });
  }

  async openSearchPrescriberDialog() {
    this.selectedPrescriber =
      await this._individualService.openIndividualSearchDialog(
        'Search for an individual',
        '',
        'VET,DOC,PHM',
        this.selectedCompany
      );

    if (!this.selectedPrescriber) return;
    this.prescriberDetailsForm.patchValue({
      firstName: this.selectedPrescriber.first_name,
      lastName: this.selectedPrescriber.last_name,
      title: this.selectedPrescriber.title,
      initials: this.selectedPrescriber.initials,
      gender: this.selectedPrescriber.gender,
      dateOfBirth: this.selectedPrescriber.date_of_birth,
      phoneNumber: this.selectedPrescriber.phone_number,
      phoneNumberAlt: this.selectedPrescriber.phone_number_alt,
      email: this.selectedPrescriber.email,
      emailAlt: this.selectedPrescriber.email_alt,
      individualType: this.selectedPrescriber.individual_type_id,
      numberOfDependants: this.selectedPrescriber.dependant_number,
    });
  }

  deselectPrescriber() {
    this.selectedPrescriber = null;
    this.prescriberDetailsForm.patchValue({
      firstName: '',
      lastName: '',
      title: '',
      initials: '',
      gender: '',
      dateOfBirth: '',
      phoneNumber: '',
      phoneNumberAlt: '',
      email: '',
      emailAlt: '',
      individualType: '',
      numberOfDependants: '',
    });
  }

  async openSearchDistributorDialog() {
    this.selectedDistributor =
      await this._companyService.openCompanySearchDialog(
        'Search for a distributor',
        '',
        'DIS'
      );

    if (!this.selectedDistributor) return;

    this.companyDetailsForm.patchValue({
      distributor: this.selectedDistributor,
    });
  }

  deselectDistributor() {
    this.selectedDistributor = null;
    this.companyDetailsForm.patchValue({
      distributor: null,
      distributorName: '',
    });
  }

  get trackable(): boolean {
    if (!this.order) return false;
    return this.waybillWasCaptured;
  }

  get labelLinks(): string[] {
    if (!this.order) return [];

    const shippingDetails: ShippingDetails = this.order.shipping_details;

    return shippingDetails.waybill_data ? shippingDetails.waybill_data : [];
  }

  viewLabel(link: string) {
    window.open(link, '_blank')?.focus();
  }

  async waybillOrder(order: Order) {
    
    const success = await this._prescriptionService.waybillOrder(order);

    if (success) await this.refresh();
  }
  async printDeliveryLabel() {
    const printDeliveryLabelPayload: PrintDeliveryLabelPayload = {
      waybillNumber: this.waybillNumber,
      address_1: this.address?.address_1,
      address_2: this.address?.address_2,
      building_apt_number: this.address?.building_apt_number.toString(),
      city: this.address?.city,
      province: this.address?.province,
      postcode: this.address?.postcode,
      country: this.address?.country
    }
    const success = await this._shippingService.printDeliveryLabel(printDeliveryLabelPayload);

    if (success) await this.refresh();
  }

  async printOrderToPDF() {
    if (!this.order) return;
    // Fetch OTP details before generating the PDF
    const otpDetails = await this.getOtpDetailsForOrder(this.order.id.toString());

    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const margin = 20;
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = margin;
    const primaryColor = '#025099';
    const fadedText = '#444444';
    const labelFontSize = 11;
    const valueFontSize = 11;
    const sectionHeaderFontSize = 13;
    const orderHeaderFontSize = 22;
    const colGap = 40;
    const colWidth = (pageWidth - margin * 2 - colGap) / 2;

    // Load logo as base64
    const logoUrl = '/assets/mbp-compoundinglogo.png';
    const logoBase64 = await new Promise<string>((resolve) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = function () {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/jpeg'));
        }
      };
      img.src = logoUrl;
    });

    // Add logo to PDF (left)
    const logoWidth = 180;
    const logoHeight = 50;
    const logoX = margin;
    const logoY = y;
    doc.addImage(logoBase64, 'JPEG', logoX, logoY, logoWidth, logoHeight);

    // Add order number to the right, vertically centered with the logo
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(orderHeaderFontSize);
    doc.setTextColor(primaryColor);
    const orderNumberY = logoY + logoHeight / 2 + orderHeaderFontSize / 2 - 4; // vertical center
    doc.text(`Order #${this.order.id}`, pageWidth - margin, orderNumberY, { align: 'right' });

    y += logoHeight + 30;

    // Company and Shipping details side by side
    let leftY = y;
    let rightY = y;
    const leftX = margin;
    const rightX = margin + colWidth + colGap;

    // --- Company Details ---
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(sectionHeaderFontSize);
    doc.setTextColor(primaryColor);
    doc.text('Company Details', leftX, leftY);
    leftY += 18;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(labelFontSize);
    doc.setTextColor(fadedText);
    if (this.company) {
      const companyFields = [
        ['Name', this.company.name],
        ['Phone', this.company.phone_number],
        ['Email', this.company.email],
        ['Alt Phone', this.company.phone_number_alt || '-'],
        ['Alt Email', this.company.email_alt || '-'],
        ['Practice #', this.company.practice_number || '-'],
        ['VAT #', this.company.vat_registration_number || '-'],
      ];
      companyFields.forEach(([label, value]) => {
        doc.setFont('helvetica', 'bold');
        doc.text(`${label}:`, leftX, leftY);
        doc.setFont('helvetica', 'normal');
        doc.text(`${value}`, leftX + 80, leftY);
        leftY += 16;
      });
    } else if (this.individual) {
      const individualFields = [
        ['Name', `${this.individual.first_name} ${this.individual.last_name}`],
        ['Phone', this.individual.phone_number],
        ['Email', this.individual.email],
        ['Alt Phone', this.individual.phone_number_alt || '-'],
        ['Alt Email', this.individual.email_alt || '-'],
      ];
      individualFields.forEach(([label, value]) => {
        doc.setFont('helvetica', 'bold');
        doc.text(`${label}:`, leftX, leftY);
        doc.setFont('helvetica', 'normal');
        doc.text(`${value}`, leftX + 80, leftY);
        leftY += 16;
      });
    }

    // --- Shipping Details ---
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(sectionHeaderFontSize);
    doc.setTextColor(primaryColor);
    doc.text('Shipping Details', rightX, rightY);
    rightY += 18;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(labelFontSize);
    doc.setTextColor(fadedText);
    if (this.order.shipping_details && this.order.shipping_details.address) {
      const address = this.order.shipping_details.address;
      const shippingFields = [
        ['Delivery Type', this.getDeliveryType(this.order)],
        ['Address 1', address.address_1],
        ['Address 2', address.address_2 || '-'],
        ['Building #', address.building_apt_number || '-'],
        ['Province', address.province || '-'],
        ['City', address.city || '-'],
        ['Postcode', address.postcode || '-'],
        ['Country', address.country || '-'],
        ['Waybill #', this.order.shipping_details.waybill_number || '-'],
      ];
      shippingFields.forEach(([label, value]) => {
        doc.setFont('helvetica', 'bold');
        doc.text(`${label}:`, rightX, rightY);
        doc.setFont('helvetica', 'normal');
        doc.text(`${value}`, rightX + 90, rightY);
        rightY += 16;
      });
    }

    // Move y to the lower of leftY or rightY, plus some space
    y = Math.max(leftY, rightY) + 20;

    // --- Customer Order Comments ---
    const comments = (this.order as any).customer_order_comments || (this.order as any).customerOrderComments;
    if (this.order && comments) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(sectionHeaderFontSize);
      doc.setTextColor(primaryColor);
      doc.text('Customer Comments:', margin, y);
      y += 16;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(labelFontSize);
      doc.setTextColor(fadedText);
      // Split comments into lines that fit the page width
      const commentLines = doc.splitTextToSize(comments, pageWidth - margin * 2);
      doc.text(commentLines, margin, y);
      y += commentLines.length * 14 + 10; // Add space after comments
    }

    // --- Order Items Table ---
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(sectionHeaderFontSize);
    doc.setTextColor(primaryColor);
    doc.text('Order Items', margin, y);
    y += 12;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(labelFontSize);
    doc.setTextColor(fadedText);
    if (this.orderItems.length > 0) {
      let total = 0;
      const body = this.orderItems.map((item, idx) => {
        const packSize = item.medicineDosagePackageType?.contained_quantity ?? '-';
        const dosageUnit = item.medicineDosagePackageType?.medicine_dosage_type?.dosage_type?.dosage_unit?.unit ?? '';
        const price = item.medicineDosagePackageType?.price ?? 0;
        const quantity = item.quantity ?? 0;
        const totalPrice = price * quantity;
        total += totalPrice;
        return [
          idx + 1,
          item.medicineDosagePackageType.label_name,
          `${packSize} ${dosageUnit}`,
          `R ${price.toFixed(2)}`,
          quantity,
          `R ${totalPrice.toFixed(2)}`
        ];
      });
      // Use a default VAT rate of 15% (standard in South Africa) since order.vat is not available
      const vatRate = 15;
      const vatTotal = total * (1 + vatRate / 100);
      const foot = [
        ['', '', '',  '', { content: `Total:`, styles: { halign: 'left' as const, fontStyle: 'bold' as const, textColor: primaryColor } },{content: `R ${total.toFixed(2)}`, styles: { halign: 'left' as const, fontStyle: 'bold' as const, textColor: primaryColor } }],
        ['', '', '',  '', { content: `incl VAT Total:`, styles: { halign: 'left' as const, fontStyle: 'bold' as const, textColor: primaryColor } },{content: `R ${vatTotal.toFixed(2)}`, styles: { halign: 'left' as const, fontStyle: 'bold' as const, textColor: primaryColor } }]
      ];
      autoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin },
        theme: 'grid',
        styles: { font: 'helvetica', fontSize: 11, cellPadding: 6, textColor: '#222' },
        headStyles: { fillColor: '#f5f5f5', textColor: '#222', fontStyle: 'bold' },
        footStyles: { fillColor: '#fff' },
        head: [['#', 'Medicine', 'Pack size', 'Price', 'Quantity', 'Total Price']],
        body,
        foot,
        bodyStyles: {
          cellPadding: { top: 4, right: 6, bottom: 4, left: 6 }
        },
      });
    }

    // Print OTP verification info at the bottom
    if (otpDetails && otpDetails.status === 'verified' && otpDetails.verified_at) {
      // Format date: '23 May 2025, 09:48'
      const dateObj = new Date(otpDetails.verified_at);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const day = dateObj.getDate().toString().padStart(2, '0');
      const month = months[dateObj.getMonth()];
      const year = dateObj.getFullYear();
      const hours = dateObj.getHours().toString().padStart(2, '0');
      const minutes = dateObj.getMinutes().toString().padStart(2, '0');
      const formattedDate = `${day} ${month} ${year}, ${hours}:${minutes}`;
      
      // Get user details from the OTP response
      const response = await this._otpService.getOtpByOrder(this.order.id.toString()).toPromise();
      let userName = 'Unknown User';
      if (response?.user) {
        const nameAttr = response.user.Attributes?.find((attr: UserAttribute) => attr.Name === 'name');
        userName = nameAttr ? nameAttr.Value : 'Unknown User';
      }
      
      const verifyText = `Verified by OTP on ${formattedDate} by ${userName}, practice ${otpDetails.practice_number ?? '-'}`;
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9);
      doc.setTextColor('#888888');
      doc.text(verifyText, margin, pageHeight - 20);
    }

    doc.save(`order-${this.order.id}.pdf`);
  }

  /**
   * Fetch OTP details for an order number
   */
  async getOtpDetailsForOrder(orderNumber: string): Promise<OtpData | null> {
    try {
      const response = await this._otpService.getOtpByOrder(orderNumber).toPromise();
      return response.otp || null;
    } catch (e) {
      return null;
    }
  }

  // Navigate back to prescriptions list preserving the search query param if present
  backToList() {
    const search = this._route.snapshot.queryParamMap.get('search');
    this._router.navigate(['/portal/prescriptions'], {
      queryParams: { search: search || undefined },
    });
  }
}